import React, { useEffect, useState } from 'react';
import { ArrowRight, PlayCircle, Cpu, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { useSite } from '../context/SiteContext';

const Hero = () => {
  const { hero } = useSite();
  const slides = (hero?.slides && hero.slides.length > 0) ? hero.slides : (hero?.feature ? [hero.feature] : []);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || slides.length <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 4200);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  if (!hero) return null;
  const active = slides[idx] || {};
  return (
    <section id="top" className="relative pt-36 pb-24 lg:pt-44 lg:pb-32 overflow-hidden">
      {/* Background layers */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-25"
        style={{
          backgroundImage: `url("${hero.backgroundImage}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(ellipse at 20% 10%, rgba(148,179,61,0.16), transparent 55%), radial-gradient(ellipse at 80% 40%, rgba(229,0,109,0.12), transparent 55%), linear-gradient(#06080d, #06080d)',
        }}
      />
      <div aria-hidden className="absolute inset-0 -z-10 bg-grid mask-radial" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs uppercase tracking-widest text-brand-green-light">
              <Zap className="h-3.5 w-3.5" /> {hero.eyebrow}
            </div>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-white">
              {hero.headingTop}{' '}
              <span className="grad-text">{hero.headingHighlight}</span>
              <br />
              {hero.headingMid} <br />
              <span className="text-brand-green-light">{hero.headingAccent}</span>{' '}
              {hero.headingEnd}
            </h1>
            <p className="mt-6 max-w-xl text-slate-300 text-lg leading-relaxed">{hero.body}</p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                className="bg-brand-green hover:bg-brand-green-light text-black font-medium h-12 px-6 btn-transition"
              >
                <a href={hero.ctaPrimary.href} className="flex items-center gap-2">
                  {hero.ctaPrimary.label} <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-12 px-6 border-white/15 bg-white/5 hover:bg-white/10 text-white btn-transition"
              >
                <a href={hero.ctaSecondary.href} className="flex items-center gap-2">
                  <PlayCircle className="h-4 w-4" /> {hero.ctaSecondary.label}
                </a>
              </Button>
            </div>

            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
              {hero.stats.map((s, i) => (
                <React.Fragment key={s}>
                  <div className="flex items-center gap-2">
                    {i === 0 && <Cpu className="h-4 w-4 text-brand-green-light" />} {s}
                  </div>
                  {i < hero.stats.length - 1 && <div className="h-1 w-1 rounded-full bg-slate-600" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Right: auto-sliding featured carousel */}
          <div className="lg:col-span-5 fade-up" style={{ animationDelay: '0.15s' }}>
            <div
              className="relative"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              <div className="absolute -inset-6 rounded-3xl bg-brand-green/10 blur-3xl" />
              <div className="absolute -bottom-8 -right-4 h-40 w-40 rounded-full bg-brand-pink/15 blur-3xl" />
              <div className="relative rounded-2xl overflow-hidden glass-strong p-4">
                <div className="rounded-xl overflow-hidden border border-white/10 relative h-64 md:h-80">
                  {slides.map((s, i) => (
                    <img
                      key={i}
                      src={s.image}
                      alt={s.title}
                      className={`absolute inset-0 w-full h-full object-cover btn-transition ${
                        i === idx ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
                      }`}
                      style={{ transitionDuration: '900ms' }}
                    />
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between min-h-[52px]">
                  <div>
                    <div className="text-xs text-slate-400 uppercase tracking-widest">{active.badge}</div>
                    <div className="text-white font-semibold">{active.title}</div>
                  </div>
                  {active.tag && (
                    <span className="text-xs px-2.5 py-1 rounded-full border border-brand-pink/50 text-brand-pink-light bg-brand-pink/10 whitespace-nowrap">
                      {active.tag}
                    </span>
                  )}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 min-h-[44px]">
                  {(active.chips || []).slice(0, 3).map((f) => (
                    <div key={f} className="rounded-lg border border-white/8 bg-white/[0.03] py-2 text-center text-xs text-slate-300">
                      {f}
                    </div>
                  ))}
                </div>

                {/* Dot indicators */}
                {slides.length > 1 && (
                  <div className="mt-4 flex items-center justify-center gap-2">
                    {slides.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setIdx(i)}
                        aria-label={`Show slide ${i + 1}`}
                        className={`h-1.5 rounded-full btn-transition ${
                          i === idx ? 'w-6 bg-brand-green' : 'w-1.5 bg-white/25 hover:bg-white/40'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
